import { useState, useEffect } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { bookingApi } from '../services/bookingApi';

/**
 * Loads the Square service catalog and resolves a single service by name.
 * Returns { service, loadError, loading }.
 *
 * Encapsulating the data-fetching here avoids triggering React 19's
 * set-state-in-effect rule at the caller site — a hook IS the right
 * place for this side effect, and the rule is opinionated about hooks-in-components.
 */
export function useSquareService(serviceName) {
  const [state, setState] = useState({ service: null, loadError: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    const update = (patch) => { if (!cancelled) setState((s) => ({ ...s, ...patch })); };

    update({ service: null, loadError: null, loading: true });

    (async () => {
      try {
        const services = await bookingApi.listServices();
        const match = findService(services, serviceName);
        if (match) {
          update({ service: match, loading: false });
        } else if (services.length === 0) {
          update({
            loadError: 'Online booking is not configured yet. Please call (505) 595-9416 to schedule your appointment.',
            loading: false,
          });
        } else {
          update({
            loadError: `We couldn't find "${serviceName}" in our booking system right now. Please call (505) 595-9416 or pick another service.`,
            loading: false,
          });
        }
      } catch (e) {
        const msg = e.response?.data?.detail
          || 'Online booking is temporarily unavailable. Please call (505) 595-9416 to book.';
        update({ loadError: msg, loading: false });
      }
    })();

    return () => { cancelled = true; };
  }, [serviceName]);

  return state;
}

export function useTaxRates() {
  const [state, setState] = useState({ taxData: null, taxError: null });

  useEffect(() => {
    let cancelled = false;
    bookingApi.getTaxRates()
      .then((taxData) => { if (!cancelled) setState({ taxData, taxError: null }); })
      .catch(() => { if (!cancelled) setState({ taxData: null, taxError: 'Could not load local tax rates. Please refresh or call (505) 595-9416.' }); });
    return () => { cancelled = true; };
  }, []);

  return state;
}

export function useSquareServices() {
  const [state, setState] = useState({ services: [], popularName: null, loadError: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [all, popular] = await Promise.all([
          bookingApi.listServices(),
          bookingApi.getPopular().catch(() => []),
        ]);
        const services = all.filter((s) => s.variations.length > 0);
        if (cancelled) return;
        const popularMatch = popular.map((name) => findService(services, name)).find(Boolean);
        setState({
          services,
          popularName: popularMatch?.name || null,
          loading: false,
          loadError: services.length === 0
            ? 'Online booking is not configured yet. Please call (505) 595-9416 to schedule your appointment.'
            : null,
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          services: [],
          loading: false,
          loadError: e.response?.data?.detail || 'Online booking is temporarily unavailable. Please call (505) 595-9416 to book.',
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}

// Match a service from Square's catalog to the card the customer clicked.
export const findService = (services, target) => {
  if (!target) return null;
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9 ]/gi, ' ').replace(/\s+/g, ' ').trim();
  const t = norm(target);
  return (
    services.find((s) => norm(s.name) === t)
    || services.find((s) => norm(s.name).includes(t))
    || services.find((s) => t.includes(norm(s.name)))
    || null
  );
};


/**
 * Loads availability slots for a service+date, cancelling stale fetches.
 */
export function useAvailability(serviceVariationId, date, dayWindow) {
  const [state, setState] = useState({ slots: [], loading: false, error: null });

  useEffect(() => {
    if (!serviceVariationId || !date) return undefined;
    let cancelled = false;
    const update = (patch) => { if (!cancelled) setState((s) => ({ ...s, ...patch })); };

    update({ slots: [], loading: true, error: null });

    (async () => {
      try {
        const { start_at, end_at } = dayWindow(date);
        const result = await bookingApi.searchAvailability({
          variation_id: serviceVariationId,
          start_at,
          end_at,
        });
        update({ slots: result, loading: false });
      } catch (e) {
        update({
          error: e.response?.data?.detail || 'Could not load availability. Please try another date.',
          loading: false,
        });
      }
    })();

    return () => { cancelled = true; };
  }, [serviceVariationId, date, dayWindow]);

  return state;
}

const SEARCH_DAYS = 21;

// Earliest calendar day (business timezone) with an opening, so the picker never opens on a full day.
export function useFirstOpenDate(serviceVariationId, timeZone) {
  const [state, setState] = useState({ date: null, loading: true });

  useEffect(() => {
    if (!serviceVariationId) return undefined;
    let cancelled = false;
    const start = new Date(Date.now() + 60 * 60 * 1000);
    const end = new Date(start.getTime() + SEARCH_DAYS * 86400000);
    bookingApi.searchAvailability({ variation_id: serviceVariationId, start_at: start.toISOString(), end_at: end.toISOString() })
      .then((slots) => {
        if (cancelled) return;
        const first = [...slots].sort((a, b) => a.start_at.localeCompare(b.start_at))[0];
        const ymd = first && formatInTimeZone(new Date(first.start_at), timeZone, 'yyyy-MM-dd');
        setState({ date: ymd ? new Date(`${ymd}T12:00:00`) : null, loading: false });
      })
      .catch(() => { if (!cancelled) setState({ date: null, loading: false }); });
    return () => { cancelled = true; };
  }, [serviceVariationId, timeZone]);

  return state;
}
